import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TSP } from '../../models';

export type AdminCompetenceQuery = TSP.Internal.Dictionary.Competence.AdminQuery;

/**
 * CRUD and manual merge decisions for the competence taxonomy, served by
 * dictionary-service through the gateway. The paid i18n pass stays on
 * AdminService — operations, not entity management.
 */
@Injectable({
  providedIn: 'root',
})
export class CompetenceService {
  private readonly http = inject(HttpClient);

  public getCompetencesList(
    query: AdminCompetenceQuery = {},
  ): Observable<TSP.Helpers.EntitiesList<TSP.Internal.Dictionary.Competence.AdminItem>> {
    const params = new HttpParams({
      fromObject: Object.fromEntries(Object.entries(query).filter(([_, value]) => value !== undefined)),
    });

    return this.http.get<TSP.Helpers.EntitiesList<TSP.Internal.Dictionary.Competence.AdminItem>>('/admin/competence', {
      params,
    });
  }

  public getMergeCandidates(): Observable<TSP.Internal.Dictionary.Competence.MergeCandidate[]> {
    return this.http.get<TSP.Internal.Dictionary.Competence.MergeCandidate[]>('/admin/competence/merge-candidates');
  }

  public getCompetence(id: number): Observable<TSP.Internal.Dictionary.Competence.Details> {
    return this.http.get<TSP.Internal.Dictionary.Competence.Details>(`/admin/competence/${id}`);
  }

  public createCompetence(
    payload: TSP.Internal.Dictionary.Competence.CreatePayload,
  ): Observable<TSP.Internal.Dictionary.Competence.Details> {
    return this.http.post<TSP.Internal.Dictionary.Competence.Details>('/admin/competence', payload);
  }

  public updateCompetence(
    id: number,
    payload: TSP.Internal.Dictionary.Competence.UpdatePayload,
  ): Observable<TSP.Internal.Dictionary.Competence.Details> {
    return this.http.put<TSP.Internal.Dictionary.Competence.Details>(`/admin/competence/${id}`, payload);
  }

  public deleteCompetence(id: number): Observable<TSP.Helpers.BooleanResponse> {
    return this.http.delete<TSP.Helpers.BooleanResponse>(`/admin/competence/${id}`);
  }

  public mergeCompetences(
    payload: TSP.Internal.Dictionary.Competence.MergePayload,
  ): Observable<TSP.Internal.Dictionary.Competence.Details> {
    return this.http.post<TSP.Internal.Dictionary.Competence.Details>('/admin/competence/merge', payload);
  }

  public rejectMerge(
    payload: TSP.Internal.Dictionary.Competence.RejectMergePayload,
  ): Observable<TSP.Helpers.BooleanResponse> {
    return this.http.post<TSP.Helpers.BooleanResponse>('/admin/competence/reject-merge', payload);
  }
}
